import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, AlertTriangle } from "lucide-react"
import { ZodError } from "zod"

import { cn } from "@/helpers/classNames"

const alertVariants = cva(
  `
    relative w-full rounded-lg border p-4
    [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground
    [&>svg+div]:translate-y-[-3px]
    [&>svg~*]:pl-7
  `,
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: `
          border-danger-100 text-danger-300
          dark:border-danger-300
          [&>svg]:text-danger-300
        `,
        critical: `
          border-danger-200 bg-danger-100
          [&>svg]:text-danger-300
        `,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Alert({
  className,
  ref,
  variant,
  ...props
}: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants>) {
  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLParagraphElement>
} & React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h5
      ref={ref}
      className={cn("mb-1 leading-none font-medium tracking-tight", className)}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLParagraphElement>
} & React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <div
      ref={ref}
      className={cn(
        `
          text-sm
          [&_p]:leading-relaxed
        `,
        className,
      )}
      {...props}
    />
  )
}

/**
 * Used to handle the error state when calling the API with `useQuery*` hooks
 * (not part of shadcn/ui)
 */
export function AlertLoadingError({
  className,
  error,
  title,
}: {
  className?: string
  error: unknown
  title: string
}) {
  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="size-5" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {error instanceof ZodError && <p>Invalid API response format</p>}
        {error instanceof Error && error.message && <p>{error.message}</p>}
      </AlertDescription>
    </Alert>
  )
}

export function AlertCritical({
  description,
  title,
}: {
  description: string
  title: string
}) {
  return (
    <Alert variant="critical">
      <AlertTriangle className="size-5 text-danger-300" />
      <AlertTitle className="mb-2 font-bold">{title}</AlertTitle>
      <AlertDescription className="text-neutral-600">
        {description}
      </AlertDescription>
    </Alert>
  )
}

export { Alert, AlertDescription, AlertTitle }
